import { useFlags } from "launchdarkly-react-client-sdk";
import { Outlet, Route } from "react-router";

import { useOrgInfoStore } from "../../store/org-info/useOrgInfoStore";
import { SubscriptionLevel } from "../../types/entitlement";
import { Page } from "../App/Page";
import { Aws } from "./Aws/Aws";
import { AwsLambda } from "./AwsLambda/AwsLambda";
import { AwsRds } from "./AwsRds/AwsRds";
import { Azure } from "./Azure/Azure";
import { EntraId as AzureAd } from "./AzureAD/EntraId";
import { Csa } from "./Csa/Csa";
import { Custom } from "./Custom/Custom";
import { Datadog } from "./Datadog/Datadog";
import { Email } from "./Email/Email";
import { EntraId } from "./EntraId/EntraId";
import { Gcloud } from "./Gcloud/Gcloud";
import { Github } from "./Github/Github";
import { GoogleWorkspace } from "./GoogleWorkspace/GoogleWorkspace";
import { IncidentIo } from "./IncidentIo/IncidentIo";
import { IntegrationsList } from "./Integrations";
import { Jira } from "./Jira/Jira";
import { K8s } from "./Kubernetes/K8s";
import { Mcp } from "./Mcp/Mcp";
import { Mysql } from "./Mysql/Mysql";
import { Okta } from "./Okta/Okta";
import { Oracle } from "./Oracle/Oracle";
import { Pagerduty } from "./Pagerduty/Pagerduty";
import { Pg } from "./Postgres/Pg";
import { Rdp } from "./Rdp/Rdp";
import { SelfHosted } from "./SelfHosted/SelfHosted";
import { Slack } from "./Slack/Slack";
import { Snowflake } from "./Snowflake/Snowflake";
import { Splunk } from "./Splunk/Splunk";
import { SplunkStack } from "./SplunkStack/SplunkStack";
import { Ssh } from "./Ssh/Ssh";
import { Tailscale } from "./Tailscale/Tailscale";
import { Teams } from "./Teams/Teams";
import { Webex } from "./Webex/Webex";
import { Windows } from "./Windows/Windows";
import { Zpa } from "./Zpa/Zpa";

export const IntegrationRoutes = () => {
  const { useSubscriptionPlan } = useOrgInfoStore();
  const subscriptionPlan = useSubscriptionPlan();

  const isRestrictedAccess =
    subscriptionPlan === SubscriptionLevel.EntitlementStarter;
  const flags = useFlags();

  return (
    <Route path="integrations">
      <Route
        index
        element={
          <Page title="Integrations">
            <IntegrationsList />
          </Page>
        }
      />
      {!isRestrictedAccess && (
        <Route
          path="pagerduty"
          element={
            <Page title="Pagerduty">
              <Pagerduty />
            </Page>
          }
        />
      )}
      {!isRestrictedAccess && (
        <Route
          path="incidentio"
          element={
            <Page title="Incident.io">
              <IncidentIo />
            </Page>
          }
        />
      )}
      {/* Note that we can't guard this behind a flag b/c it has to be accessible prior to login */}
      <Route
        path="jira"
        element={
          <Page title="Jira">
            <Jira />
          </Page>
        }
      />
      <Route
        path="slack"
        element={
          <Page title="Slack">
            <Slack />
          </Page>
        }
      />
      <Route
        path="ms-teams"
        element={
          <Page title="Microsoft Teams">
            <Teams />
          </Page>
        }
      />
      <Route
        path="webex"
        element={
          <Page title="Webex">
            <Webex />
          </Page>
        }
      />
      <Route
        path="email"
        element={
          <Page title="Email">
            <Email />
          </Page>
        }
      />
      {!isRestrictedAccess && (
        <Route
          path="workspace"
          element={
            <Page title="Google Workspace">
              <GoogleWorkspace />
            </Page>
          }
        />
      )}
      {!isRestrictedAccess && (
        <Route
          path="azure-ad"
          element={
            <Page title="Entra ID">
              <AzureAd />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      {!isRestrictedAccess && (
        <Route
          path="entra-id"
          element={
            <Page title="Entra ID">
              <EntraId />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      {!isRestrictedAccess && (
        <Route
          path="okta"
          element={
            <Page title="Okta">
              <Okta />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      <Route
        path="aws"
        element={
          <Page title="AWS">
            <Aws />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="aws-rds"
        element={
          <Page title="AWS RDS">
            <AwsRds />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="azure"
        element={
          <Page title="Azure">
            <Azure />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="gcloud"
        element={
          <Page title="Google Cloud">
            <Gcloud />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="github"
        element={
          <Page title="GitHub">
            <Github />
          </Page>
        }
      />
      <Route
        path="k8s"
        element={
          <Page title="Kubernetes">
            <K8s />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="mysql"
        element={
          <Page title="MySQL">
            <Mysql />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="mcp"
        element={
          <Page title="MCP">
            <Mcp />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      {/* Note that we can't guard this behind a flag b/c it has to be accessible prior to login */}
      <Route
        path="custom"
        element={
          <Page title="Custom">
            <Custom />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="splunk-stack"
        element={
          <Page title="Splunk Stack Access">
            <SplunkStack />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      {flags.selfHostedIntegration && (
        <Route
          path="self-hosted"
          element={
            <Page title="Self-hosted">
              <SelfHosted />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      <Route
        path="aws-lambda-notifier"
        element={
          <Page title="AWS Lambda Notifier">
            <AwsLambda />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="snowflake"
        element={
          <Page title="Snowflake">
            <Snowflake />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="pg"
        element={
          <Page title="PostgreSQL">
            <Pg />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="ssh"
        element={
          <Page title="SSH">
            <Ssh />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="rdp"
        element={
          <Page title="RDP">
            <Rdp />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="windows"
        element={
          <Page title="Windows">
            <Windows />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="oci"
        element={
          <Page title="Oracle Cloud">
            <Oracle />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="splunk"
        element={
          <Page title="Splunk">
            <Splunk />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      <Route
        path="datadog"
        element={
          <Page title="Datadog">
            <Datadog />
          </Page>
        }
      >
        <Route path=":component" element={<Outlet />}>
          <Route path=":id" element={null} />
        </Route>
      </Route>
      {flags.tailscaleIntegration && (
        <Route
          path="tailscale"
          element={
            <Page title="Tailscale">
              <Tailscale />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      {flags.enableZscalerIntegration && (
        <Route
          path="zscaler-private-access"
          element={
            <Page title="Zscaler Private Access">
              <Zpa />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
      {flags.enableCiscoSecureAccessIntegration && (
        <Route
          path="cisco-secure-access"
          element={
            <Page title="Cisco Secure Access">
              <Csa />
            </Page>
          }
        >
          <Route path=":component" element={<Outlet />}>
            <Route path=":id" element={null} />
          </Route>
        </Route>
      )}
    </Route>
  );
};
